/**
 * Contact Form Code — iframe embed snippets for the four website enquiry
 * forms (general training + ProBound / ProEpoxy / ProDecor course forms).
 * Moved out of the Training Enquiries inbox into its own page.
 */
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Code2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { OxFormEditor } from '@/components/staff/OxFormEditor';

const EMBEDS = [
  { key: 'training', label: 'General training enquiry', path: '/embed/training-enquiry', height: 780 },
  { key: 'probound', label: 'ProBound course enquiry', path: '/embed/probound-enquiry', height: 880 },
  { key: 'proepoxy', label: 'ProEpoxy course enquiry', path: '/embed/proepoxy-enquiry', height: 880 },
  { key: 'prodecor', label: 'ProDecor course enquiry', path: '/embed/prodecor-enquiry', height: 880 },
];

export default function StaffContactFormCode() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="contact-code-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <Code2 className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Contact Form Code
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Copy an embed and paste it onto any page of the training website</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 space-y-5">
        <div>
          <h2 className="text-sm font-bold text-gray-900 mb-2">Edit form wording</h2>
          <p className="text-xs text-gray-500 mb-3">Change headlines, intro text, button labels and which fields show. Saves apply to the live embedded forms instantly — no need to re-paste code.</p>
          <div className="space-y-2">
            {EMBEDS.map((e) => <OxFormEditor key={e.key} formKey={e.key} label={e.label} path={e.path} />)}
          </div>
        </div>
        <EmbedSnippetCard />
      </div>
    </div>
  );
}

export function EmbedSnippetCard() {
  const [copied, setCopied] = useState('');
  // All four embed forms — labelled for the user. Built off window.location.origin
  // so they automatically rewrite to whatever domain the staff portal is on.
  const origin = window.location.origin;
  const embeds = EMBEDS;

  const snippetFor = (e) => `<!-- Ox Resin ${e.label} form — paste as many times as you like, each one renders -->
<div class="ox-form" data-ox-form="${e.key}"></div>
<script src="${API_URL}/api/public/embed-form/${e.key}.js" async></script>`;
  const legacyFor = (e) => `<iframe src="${origin}${e.path}" title="${e.label}" style="width:100%;min-height:${e.height}px;border:0;border-radius:14px;" loading="lazy"></iframe>`;
  const [showLegacy, setShowLegacy] = useState(false);

  const copy = async (e) => {
    try {
      await navigator.clipboard.writeText(snippetFor(e));
      setCopied(e.key);
      toast.success(`${e.label} embed copied — paste it onto the page.`);
      setTimeout(() => setCopied(''), 2000);
    } catch {
      toast.error('Couldn\u2019t copy — please select the text manually.');
    }
  };

  return (
    <Card data-testid="training-embed-card">
      <div className="px-4 py-3 bg-gray-100 border-b border-gray-200">
        <h3 className="text-sm font-bold text-gray-900">Embed code — paste onto the page where the form should appear</h3>
        <p className="text-[11px] text-gray-500 mt-0.5">Raw code: the form renders instantly in the page (one small script, no iframe). Wording edits above apply within 5 minutes — no need to re-paste. All four email <strong>training@oxresin.co.uk</strong>.</p>
        <button type="button" onClick={() => setShowLegacy((v) => !v)} className="text-[11px] underline text-gray-500 mt-1" data-testid="embed-legacy-toggle">{showLegacy ? 'Hide' : 'Show'} old iframe code</button>
      </div>
      <div className="divide-y divide-gray-100">
        {embeds.map((e) => (
          <div key={e.key} data-testid={`embed-block-${e.key}`} className="p-3 sm:p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-sm font-semibold text-gray-900">{e.label}</div>
              <Button
                onClick={() => copy(e)}
                size="sm"
                data-testid={`embed-copy-${e.key}`}
                variant="outline"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
              >
                {copied === e.key ? <><Check className="w-3.5 h-3.5 mr-1" /> Copied</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Copy</>}
              </Button>
            </div>
            <pre data-testid={`embed-snippet-${e.key}`} className="text-[11px] sm:text-xs text-gray-700 bg-gray-50 border border-gray-100 p-3 rounded-md overflow-auto whitespace-pre-wrap break-all m-0">
{snippetFor(e)}
            </pre>
            {showLegacy && <pre data-testid={`embed-legacy-${e.key}`} className="mt-2 text-[10px] text-gray-500 bg-gray-50 border border-dashed border-gray-200 p-2 rounded-md overflow-auto whitespace-pre-wrap break-all m-0">{legacyFor(e)}</pre>}
          </div>
        ))}
      </div>
    </Card>
  );
}
